import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { SignalsService } from '@shared/services/signals/signals.service';
import { AppService } from 'app/app.service';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { AdminService } from './admin.service';
import { environment } from '@environments/environment';
import { forkJoin, map, Observable, of, tap } from 'rxjs';
import { ProgressBarModule } from 'primeng/progressbar';
import { SkeletonModule } from 'primeng/skeleton';
import { ApiStatus } from './model/api-status';
import { ApiCacheService } from '@shared/services/cache-service/api-cache.service';
import { MemberDetail } from '../flights/flight-detail/model/crew-detail';
import { DialogModule } from 'primeng/dialog';
import { DataItem } from '@shared/models/data-item';
import { DataBoxComponent } from "../../shared/components/data-box/data-box.component";
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DateTime } from 'luxon';

@Component({
  selector: 'app-admin',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    ProgressBarModule,
    SkeletonModule,
    DialogModule,
    DataBoxComponent,
    ConfirmPopupModule
  ],
  templateUrl: './admin.component.html',
  styleUrl: './admin.component.scss'
})
export class AdminComponent {

  private readonly _crewBasicUrl = `${environment.crewBasicUrl}`

  form: FormGroup = new FormGroup('');
  apiStatus$ = this.getApiStatus();
  startTime = DateTime.now().toMillis();
  theme$ = this.appService.theme$;
  lastUpdated: string = '';

  cacheInfoDialog = false;
  errorInfoDialog = false;


  dataItem: DataItem[] = []

  constructor(
    public appService: AppService,
    public signalsService: SignalsService,
    private router: Router,
    public adminService: AdminService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private cacheServie: ApiCacheService
  ) { }

  ngOnInit(): void {
    this.form = new FormGroup({
      employeeNumber: new FormControl<string | null>(null, [Validators.required, Validators.minLength(6)]),
    });
    const apiStatusChecked = sessionStorage.getItem('apiStatusChecked');
    const dateString = apiStatusChecked ? JSON.parse(apiStatusChecked) : '';
    this.lastUpdated = DateTime.fromISO(dateString, { zone: 'utc' }).toFormat('yyyy-MM-dd HH:mm');
  }

  get employeeNumber() {
    return this.form.get('employeeNumber');
  }

  onSubmit(): void {
    this.getEmulatedPilotData(this.employeeNumber?.value);
  }

  onReset(): void {
    this.adminService.resetEmulatedPilot();
  }

  numeric(control: AbstractControl): void {
    control.setValue((control.value as string).replace(/\D/, ''));
  }

  /**
     * Fetches emulated pilot data for a given employee number and stores it in session storage.
     * 
     * @param empNumber - The employee number of the pilot to emulate.
     * 
     * This method constructs a URL using the base crew URL and sends a request to fetch the pilot's details.
     * The response is cached and stored in session storage under the key 'emulatedPilot'.
     * If the response contains an error or is not loading, an error message is logged to the console.
     * Upon successful completion of the request, the user is navigated to the '/flights' route.
     */
  getEmulatedPilotData(empNumber: string): void {
    const url = `${this._crewBasicUrl}`
    const empNum: string[] = [empNumber];
    this.adminService.clearActiveEmpNumCache();
    this.cacheServie.request<MemberDetail>('emulatedPilot', url, undefined, empNum)
      .subscribe({
        next: (response) => {
          if ((!response.response || response.hasError) && !response.isLoading) {
            console.error('No response from server flight detail service');
            return;
          }
        },
        complete: () => {
          this.signalsService.isEumulatingEmpNumActive$.set(true);
          this.signalsService._empNumberObs.next(this.employeeNumber?.value);
          this.router.navigate(['/flights']);
        }
      });
  }

  getApiStatus(): Observable<ApiStatus[]> {
    this.adminService.isLoadingProgressBar.set(true);
    const apiStatusChecked = sessionStorage.getItem('apiStatusChecked');
    const apiStatus = sessionStorage.getItem('apiStatus');

    if (apiStatusChecked) {
      const timeChecked = JSON.parse(apiStatusChecked);
      const currentTime = DateTime.now().toISO();
      const timeDiff = DateTime.fromISO(currentTime).diff(DateTime.fromISO(timeChecked), 'minutes').minutes;
      if (timeDiff > 5) {
        return this.adminService.checkApiStatus();
      }
      const _apiStatus: ApiStatus[] = JSON.parse(apiStatus || '[]');
      this.adminService.isLoadingProgressBar.set(false);
      return of(
        _apiStatus
      );


    }
    return this.adminService.checkApiStatus();
  }

  refreshApiStatus(): void {
    this.adminService.isLoadingProgressBar.set(true);
    this.lastUpdated = DateTime.now().toFormat('yyyy-MM-dd HH:mm');
    this.apiStatus$ = this.adminService.checkApiStatus()
      .pipe(
        tap({
          complete: () => {
            this.lastUpdated = DateTime.now().toFormat('yyyy-MM-dd HH:mm');
          }
        })
      );
  }

  toggleCacheHelp(): void {
    this.cacheInfoDialog = true;
  }

  toggleErrorHelp(): void {
    this.errorInfoDialog = true;
  }

  clearCache(): void {
    this.adminService.clearActiveEmpNumCache();
    this.adminService.clearSessionStorage();
  }

  clearErrorLogs(): void {
    this.adminService.clearErrorLogs();
  }

  statusViewer(type: string): void {
    if (type === 'local') {
      sessionStorage.setItem('to', JSON.stringify('cache-viewer'));
      this.router.navigate(['admin/cache-viewer', 'local']);
    }
    else if (type === 'session') {
      sessionStorage.setItem('to', JSON.stringify('cache-viewer'));
      this.router.navigate(['admin/cache-viewer', 'session']);
    }
    else if (type === 'error') {
      sessionStorage.setItem('to', JSON.stringify('cache-viewer'));
      this.router.navigate(['admin/cache-viewer', 'error']);
    }
  }

  confirmClearCache(event: Event): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Are you sure you want to proceed?',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.clearCache();
        this.messageService.add({ severity: 'success', summary: 'Confirmed', detail: 'Storage has been cleared.', life: 3000 });
      },
      reject: () => {
        //this.messageService.add({ severity: 'error', summary: 'Rejected', detail: 'You have rejected', life: 3000 });
      }
    });
  }

  confirmClearLogs(event: Event): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Are you sure you want to proceed?',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.clearErrorLogs();
        this.messageService.add({ severity: 'success', summary: 'Confirmed', detail: 'Error Log has been cleared.', life: 3000 });
      },
      reject: () => {
        //this.messageService.add({ severity: 'error', summary: 'Rejected', detail: 'You have rejected', life: 3000 });
      }
    });
  }

  titleLocalCache = "Local Cache";
  titleSessionCache = "Session Cache";
  titleClearCache = "Clear Cache";
  titleErrorLogs = "Error Logs";
  titleClearErrorLogs = "Clear Error Logs";

  localCache: DataItem[] = [
    {
      label: "",
      value: `Select to view frequently used local storage that remains in cache 
      until it expires, is reloaded, or is cleared when the app is updated. 
      Temporary data cache, in some cases, is cleared upon navigation.`
    }
  ];

  sessionCache: DataItem[] = [
    {
      label: "",
      value: `Select to view the session storage, it is equal to the local storage 
      object, except that it stores the data for only one session. The data is deleted 
      when the user closes the app, specific browser tab or, in some cases, the user 
      navigates to a different page in the app.`
    }
  ];

  clearCached: DataItem[] = [
    {
      label: "",
      value: `USE WITH CAUTION!  Select this option to clear all local and session cache data. Ceratin 
      local and session storage cache that is required for the app functionality will
      not be removed.`
    }
  ];

  errorLogs: DataItem[] = [
    {
      label: "",
      value: `Error logs are used to store any errors that occur during the application
      runtime.  This is used to help troubleshoot and fix any issues that may occur.`
    }
  ];

  clearError: DataItem[] = [
    {
      label: "",
      value: `Select this option to clear all error logs that have been stored in the  
      session storage.  Use when troubleshooting and fixing issues that may occur.`
    }
  ];

}
